import { Box, Skeleton, Stack, styled } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";

import { PAGE_PADDING, gutterFor } from "@/constants/layout";

/**
 * `nS` — what the original's menu page renders while its catalog is empty.
 *
 * This is the ONLY thing the original shows in that state: `z = 0 === i.length
 * ? <nS isModernDesign={A}/> : …`. There is no "nothing to show" message and no
 * error panel anywhere on the route — its loader (`eu` in the menu page chunk)
 * swallows every failure except an expired token, so a menu that never arrives
 * simply keeps these skeletons on screen.
 *
 * We reproduce the skeletons and, unlike the original, ALSO surface the failure
 * through the original's own snackbar channel — see `menu.tsx`. That is a
 * deliberate, recorded deviation: it uses the original's mechanism and the
 * original's copy, and it is the difference between "this tenant has no items"
 * and "the request failed", which the original genuinely cannot tell you.
 *
 * Eight square tiles, a 130px band, then eight horizontal rows with a 140px
 * thumbnail — i.e. one block per grid family the catalog can render.
 */

/** `nP` — the gutter frame. Honours the 12% modern `xxxl` gutter. */
const Frame = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isModernDesign",
})<{ isModernDesign?: boolean }>(({ theme, isModernDesign = false }) => {
  const gutter = gutterFor(isModernDesign);
  return {
    padding: `0 ${PAGE_PADDING}px`,
    flex: 1,
    [theme.breakpoints.up("lg")]: { padding: `0 ${gutter.lg}` },
    [theme.breakpoints.up("xl")]: { padding: `0 ${gutter.xl}` },
    [theme.breakpoints.up("xxl")]: { padding: `0 ${gutter.xxl}` },
    [theme.breakpoints.up("xxxl")]: { padding: `0 ${gutter.xxxl}` },
  };
});

/** `nC` */
const Cell = styled(Stack)({
  position: "relative",
  width: "100%",
  height: "100%",
});

const EIGHT = Array.from({ length: 8 }, (unused, index) => index);

export default function CatalogSkeleton({
  isModernDesign,
}: {
  isModernDesign?: boolean;
}) {
  return (
    <Frame isModernDesign={isModernDesign}>
      <Stack spacing={3}>
        <Box>
          <Grid
            container
            columnSpacing={{ xxs: 2, xxl: 4, xxxl: 6 }}
            rowSpacing={{ xxs: 2, xxl: 4 }}
          >
            {EIGHT.map((index) => (
              <Grid key={index} xxs={6} xs={4} sm={3} md={3} lg={4} xl={3}>
                <Cell spacing={0.5}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={0}
                    sx={{ paddingTop: "100%", position: "relative" }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Skeleton variant="text" height={16} width="100%" />
                  </div>
                  <div>
                    <Skeleton variant="text" height={14} width="70%" />
                  </div>
                </Cell>
              </Grid>
            ))}
          </Grid>
        </Box>
        <br />
        <Box>
          <Skeleton variant="rounded" height={130} />
          <br />
          <Grid
            container
            columnSpacing={{ xxs: 2, xxl: 4, xxxl: 6 }}
            rowSpacing={{ xxs: 2, xxl: 4 }}
          >
            {EIGHT.map((index) => (
              <Grid key={index} xxs={12} xs={6}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Stack spacing={1} sx={{ width: "100%", flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" height={16} />
                      <Skeleton variant="text" height={16} width="80%" />
                      <Skeleton variant="text" height={16} width="40%" />
                    </Stack>
                    <Skeleton
                      variant="rounded"
                      height={140}
                      width={140}
                      sx={{ minWidth: 140, minHeight: 140, flexShrink: 0 }}
                    />
                  </Stack>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
      <br />
      <br />
    </Frame>
  );
}
